import React, { useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import {
    Button,
    Color,
    Modal,
    ModalContentBody,
    ModalContentFooter,
    ModalContentHeader,
    Size,
    TypographyColor,
    Variant,
} from '@signaturit/react-components-library';
import { ROOT_URL } from '../../constants/endpoints';
import { getCurrentLanguage } from '../../../helpers/LangHelper';

import { useLocalStorageEvents } from '../../../hooks/useLocalStorageEvents/useLocalStorageEvents';
import { IdleStorageKey, IdleStorageValue } from '../../../constants/IdleStorage';
import { Countdown } from './Countdown';
import { getCountdownTimeMsTimestamp } from './countdownHelper';
import { Typography } from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';
import { useIdleTimerContainerStyles } from './IdleTimerContainer.styles';

export const IDLE_TIMER_CONTAINER_DATA_SIGNA_PREFIX = 'idle-timer';

export const IdleTimerContainer = () => {
    const classes = useIdleTimerContainerStyles();
    const { t } = useTranslation();
    const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [countdownTimestampMs, setCountdownTimestampMs] = useState<number>(0);
    const { setItemStorage, eventListenerStorage, removeEventListenerStorage } = useLocalStorageEvents();
    const defaultIdleTimeToShowModalInMilliseconds = '10000';
    const defaultTimeToCloseModalAndLogoutInMinutes = '1';
    const timeout = parseInt(process.env.IDLE_TIME_TO_SHOW_MODAL_IN_MS || defaultIdleTimeToShowModalInMilliseconds);
    const timeToCloseModalAndLogout = parseInt(
        process.env.IDLE_TIME_TO_CLOSE_MODAL_AND_LOGOUT_IN_M || defaultTimeToCloseModalAndLogoutInMinutes
    );
    const onIdle = (): void => {
        if (!isModalOpened) {
            setIsModalOpened(true);
            setCountdownTimestampMs(getCountdownTimeMsTimestamp(timeToCloseModalAndLogout));
            setItemStorage(IdleStorageKey.TimeoutModal, IdleStorageValue.TimeoutModalOpened);
            setItemStorage(IdleStorageKey.TimeoutLogout, IdleStorageValue.UserIsLogin);
        }
    };

    useIdleTimer({
        timeout,
        onIdle,
        crossTab: {
            emitOnAllTabs: true,
        },
    });

    const onClose = (): void => {
        setItemStorage(IdleStorageKey.TimeoutModal, IdleStorageValue.TimeoutModalClosed);
        setIsModalOpened(false);
        clearInterval(countdownTimerRef.current as NodeJS.Timeout);
    };

    const onLogout = (): void => {
        setItemStorage(IdleStorageKey.TimeoutLogout, IdleStorageValue.UserIsLogout);
        window.location.href = `${ROOT_URL}/${getCurrentLanguage()}/logout`;
        setIsModalOpened(false);
        clearInterval(countdownTimerRef.current as NodeJS.Timeout);
    };

    useEffect(() => {
        eventListenerStorage(IdleStorageKey.TimeoutModal, IdleStorageValue.TimeoutModalClosed, onClose);
        eventListenerStorage(IdleStorageKey.TimeoutLogout, IdleStorageValue.UserIsLogout, onLogout);
        return () => {
            removeEventListenerStorage(IdleStorageKey.TimeoutModal, IdleStorageValue.TimeoutModalClosed, onClose);
            removeEventListenerStorage(IdleStorageKey.TimeoutLogout, IdleStorageValue.UserIsLogout, onLogout);
        };
    }, []);

    return (
        <>
            <Modal
                opened={isModalOpened}
                hideCloseButton={false}
                onClickClose={onClose}
                size={Size.small}
                dataName={`${IDLE_TIMER_CONTAINER_DATA_SIGNA_PREFIX}-modal`}
            >
                <ModalContentHeader>{t('default.idle_timer.title')}</ModalContentHeader>
                <ModalContentBody>
                    <Typography
                        variant="body2"
                        color={TypographyColor.textPrimary}
                        className={classes.body}
                        data-signa={`${IDLE_TIMER_CONTAINER_DATA_SIGNA_PREFIX}-body`}
                    >
                        <Trans
                            i18nKey={'default.idle_timer.body'}
                            components={[
                                <Countdown countdownTimestampMs={countdownTimestampMs} onCountdown={onLogout} />,
                            ]}
                        />
                    </Typography>
                </ModalContentBody>
                <ModalContentFooter>
                    <span className={classes.logoutButton}>
                        <Button
                            variant={Variant.text}
                            color={Color.tertiary}
                            onClick={onLogout}
                            name={`${IDLE_TIMER_CONTAINER_DATA_SIGNA_PREFIX}-log-out`}
                        >
                            {t('default.idle_timer.logout_button')}
                        </Button>
                    </span>
                    <Button onClick={onClose} name={`${IDLE_TIMER_CONTAINER_DATA_SIGNA_PREFIX}-remain-active`}>
                        {t('default.idle_timer.remain_active_button')}
                    </Button>
                </ModalContentFooter>
            </Modal>
        </>
    );
};
